import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import adminInventoryApi from "../services/adminInventoryApi";
import adminLogisticsApi from "../services/adminLogisticsApi";
import "../styles/Admin.css";

const ACTION_OPTIONS = [
  { value: "", label: "All pending actions" },
  { value: "CREATE", label: "CREATE" },
  { value: "SHIP", label: "SHIP" },
  { value: "ARRIVE", label: "ARRIVE" },
  { value: "RECEIVE", label: "RECEIVE" },
  { value: "CANCEL", label: "CANCEL" },
];

export default function AdminShipmentApprovalsPage({ adminEmail, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const [branches, setBranches] = useState([]);
  const [shipments, setShipments] = useState([]);

  // Filters (do not auto-apply: only apply on button click)
  const [status, setStatus] = useState("");
  const [pendingAction, setPendingAction] = useState("");
  const [originBranchId, setOriginBranchId] = useState("");
  const [destinationBranchId, setDestinationBranchId] = useState("");

  const [noteById, setNoteById] = useState({});

  const branchById = useMemo(() => {
    const m = new Map();
    (branches || []).forEach((b) => m.set(String(b.id), b));
    return m;
  }, [branches]);

  function formatTs(v) {
    if (!v) return "-";
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return String(v);
      return d.toLocaleString();
    } catch {
      return String(v);
    }
  }

  async function loadBranches() {
    const res = await adminInventoryApi.listBranches().catch(() => null);
    if (!res?.ok) throw new Error(res?.error || "Failed to load branches");
    setBranches(Array.isArray(res.branches) ? res.branches : []);
  }

  async function loadPending(opts = {}) {
    setError("");
    setOkMsg("");
    const params = {
      limit: 200,
      offset: 0,
      pendingStatus: "pending",
      status: opts.status || undefined,
      pendingAction: opts.pendingAction || undefined,
      originBranchId: opts.originBranchId || undefined,
      destinationBranchId: opts.destinationBranchId || undefined,
    };
    const res = await adminLogisticsApi.listShipments(params).catch(() => null);
    if (!res?.ok) throw new Error(res?.error || "Failed to load pending shipments");
    const list = res.shipments ?? res.rows ?? [];
    setShipments(Array.isArray(list) ? list : []);
  }

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        await loadBranches();
        await loadPending({});
      } catch (e) {
        if (mounted) setError(e?.message || "Failed to load");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  async function applyFilters() {
    try {
      setLoading(true);
      await loadPending({ status, pendingAction, originBranchId, destinationBranchId });
    } catch (e) {
      setError(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  async function clearFilters() {
    setStatus("");
    setPendingAction("");
    setOriginBranchId("");
    setDestinationBranchId("");
    try {
      setLoading(true);
      await loadPending({});
    } catch (e) {
      setError(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  async function approve(shipmentId) {
    setBusyId(shipmentId);
    setError("");
    setOkMsg("");
    try {
      const note = (noteById[shipmentId] || "").trim();
      const res = await adminLogisticsApi.approvePendingShipment(shipmentId, { note }).catch(() => null);
      if (!res?.ok) throw new Error(res?.error || "Approve failed");
      await loadPending({ status, pendingAction, originBranchId, destinationBranchId });
      setNoteById((p) => ({ ...p, [shipmentId]: "" }));
      setOkMsg("Approved.");
    } catch (e) {
      setError(e?.message || "Approve failed");
    } finally {
      setBusyId(null);
    }
  }

  async function reject(shipmentId) {
    setBusyId(shipmentId);
    setError("");
    setOkMsg("");
    try {
      const note = (noteById[shipmentId] || "").trim();
      const res = await adminLogisticsApi.rejectPendingShipment(shipmentId, { note }).catch(() => null);
      if (!res?.ok) throw new Error(res?.error || "Reject failed");
      await loadPending({ status, pendingAction, originBranchId, destinationBranchId });
      setNoteById((p) => ({ ...p, [shipmentId]: "" }));
      setOkMsg("Rejected.");
    } catch (e) {
      setError(e?.message || "Reject failed");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">Shipment Approvals</h1>
            <p className="ucx-admin-subtitle">Review and approve/reject pending shipment actions.</p>
          </div>

          <div className="ucx-admin-header-right">
            {adminEmail && <span className="ucx-small">Signed in as {adminEmail}</span>}
            <div className="ucx-admin-header-actions">
              <button
                type="button"
                className="ucx-btn ucx-btn-outline"
                onClick={() => applyFilters()}
                disabled={loading}
              >
                {loading ? "Refreshing…" : "Refresh"}
              </button>
              <button type="button" className="ucx-admin-btn ucx-admin-btn-reject" onClick={onLogout}>
                Sign out
              </button>
            </div>
          </div>
        </div>

        {error ? <div className="ucx-admin-status ucx-admin-status-error">{error}</div> : null}
        {okMsg ? <div className="ucx-admin-status ucx-admin-status-success">{okMsg}</div> : null}

        <div className="ucx-admin-panel">
          <div className="ucx-admin-toolbar">
            <div>
              <div className="ucx-admin-panel-title" style={{ marginBottom: 2 }}>
                Filters
              </div>
              <div className="ucx-admin-toolbar-hint">Applies only when you click Apply.</div>
            </div>

            <div className="ucx-admin-filters ucx-admin-filters-inventory">
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All statuses</option>
                <option value="draft">draft</option>
                <option value="ready">ready</option>
                <option value="in_transit">in_transit</option>
                <option value="delivered">delivered</option>
                <option value="cancelled">cancelled</option>
              </select>

              <select value={pendingAction} onChange={(e) => setPendingAction(e.target.value)}>
                {ACTION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>

              <select value={originBranchId} onChange={(e) => setOriginBranchId(e.target.value)}>
                <option value="">All origin branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={String(b.id)}>
                    {b.name}
                  </option>
                ))}
              </select>

              <select value={destinationBranchId} onChange={(e) => setDestinationBranchId(e.target.value)}>
                <option value="">All destination branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={String(b.id)}>
                    {b.name}
                  </option>
                ))}
              </select>

              <button type="button" className="ucx-btn ucx-btn-outline" onClick={applyFilters} disabled={loading}>
                Apply
              </button>
              <button
                type="button"
                className="ucx-btn ucx-btn-outline ucx-admin-clear-filters"
                onClick={clearFilters}
                disabled={loading}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-toolbar">
            <div>
              <div className="ucx-admin-panel-title" style={{ marginBottom: 2 }}>
                Pending approvals
              </div>
              <div className="ucx-admin-toolbar-hint">{loading ? "Loading…" : `${shipments.length} pending`}</div>
            </div>
          </div>

          <div className="ucx-admin-table-wrapper">
            <table className="ucx-admin-table">
              <thead>
                <tr>
                  <th>Shipment</th>
                  <th>Pending action</th>
                  <th>Requested</th>
                  <th>From → To</th>
                  <th>Note (audit)</th>
                  <th style={{ width: 220 }}>Decision</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="ucx-admin-empty">
                      Loading…
                    </td>
                  </tr>
                ) : shipments.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="ucx-admin-empty">
                      No pending approvals.
                    </td>
                  </tr>
                ) : (
                  shipments.map((s) => {
                    const fromB = branchById.get(String(s.origin_branch_id));
                    const toB = branchById.get(String(s.destination_branch_id));
                    const isBusy = busyId === s.id;

                    return (
                      <tr key={s.id}>
                        <td>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <strong>{s.shipment_no || "-"}</strong>
                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                              <Link className="ucx-btn ucx-btn-outline" to={`/logistics/shipments/${s.id}`}>
                                View
                              </Link>
                              <span className="ucx-small" style={{ opacity: 0.8 }}>
                                ID: {s.id}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <span className="ucx-badge">{s.pending_action || "-"}</span>
                            <span className="ucx-small" style={{ opacity: 0.8 }}>
                              Current status: {s.status || "-"}
                            </span>
                          </div>
                        </td>

                        <td>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <div>{formatTs(s.pending_requested_at)}</div>
                            <div className="ucx-small" style={{ opacity: 0.8 }}>
                              By user: {s.pending_requested_by_user_id ?? "-"}
                            </div>
                          </div>
                        </td>

                        <td>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <div>
                              {fromB ? fromB.name : `#${s.origin_branch_id}`} → {toB ? toB.name : `#${s.destination_branch_id}`}
                            </div>
                            <div className="ucx-small" style={{ opacity: 0.8 }}>
                              Locations: #{s.origin_location_id} → #{s.destination_location_id}
                            </div>
                          </div>
                        </td>

                        <td>
                          <input
                            className="ucx-admin-input-compact"
                            placeholder="Optional note (stored in events)"
                            value={noteById[s.id] || ""}
                            onChange={(e) => setNoteById((p) => ({ ...p, [s.id]: e.target.value }))}
                            disabled={isBusy}
                          />
                        </td>

                        <td>
                          <div className="ucx-admin-actions">
                            <button
                              type="button"
                              className="ucx-admin-btn ucx-admin-btn-approve"
                              onClick={() => approve(s.id)}
                              disabled={isBusy}
                            >
                              {isBusy ? "Working…" : "Approve"}
                            </button>
                            <button
                              type="button"
                              className="ucx-admin-btn ucx-admin-btn-reject"
                              onClick={() => reject(s.id)}
                              disabled={isBusy}
                            >
                              {isBusy ? "Working…" : "Reject"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="ucx-admin-toolbar-hint" style={{ marginTop: 12 }}>
            Tip: approve/reject writes into the shipment timeline via <code>shipment_events</code> (REQUESTED / APPROVED / REJECTED).
          </div>
        </div>
      </div>
    </section>
  );
}